import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { Trash2, Plus, Minus, ShoppingBag, FileText, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';

export default function CartPage() {
  usePageTitle("Panier — Lodiss");

  const { lang, cart, removeFromCart, updateQty, setPage } = useApp();
  const T = t[lang];

  const hasQuoteLines = cart.some(item => item.qty >= item.product.quoteThreshold);
  const hasDirectLines = cart.some(item => item.qty < item.product.quoteThreshold);
  const isMixed = hasQuoteLines && hasDirectLines;

  const [mode, setMode] = useState<'direct' | 'quote'>(hasQuoteLines ? 'quote' : 'direct');

  const directTotal = cart
    .filter(i => i.qty < i.product.quoteThreshold)
    .reduce((sum, i) => sum + i.product.price * i.qty, 0);

  const allTotal = cart.reduce((sum, i) => sum + i.product.price * i.qty, 0);

  if (cart.length === 0) {
    return (
      <PageWrapper>
      <main className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-20">
          <ShoppingBag size={48} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 mb-6">{T.cart.empty}</p>
          <button
            onClick={() => setPage('catalogue')}
            className="px-6 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white rounded-lg font-semibold text-sm transition-colors"
          >
            {T.cart.goCatalogue}
          </button>
        </div>
      </main>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-950 mb-6">{T.cart.title}</h1>

        {isMixed && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-amber-800 font-medium mb-3">{T.cart.mixedNote}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setMode('direct')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                  mode === 'direct'
                    ? 'border-lodiss-red bg-lodiss-red text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                {T.cart.toggleDirect}
              </button>
              <button
                onClick={() => setMode('quote')}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                  mode === 'quote'
                    ? 'border-amber-500 bg-amber-500 text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                {T.cart.toggleQuote}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {lang === 'fr' ? 'Produit' : 'Product'}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">
                  {T.cart.qty}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">
                  {T.cart.unit}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {T.cart.subtotal}
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {cart.map(item => {
                const isQuote = item.qty >= item.product.quoteThreshold;
                const name = lang === 'fr' ? item.product.name : item.product.nameEn;
                return (
                  <tr key={item.product.id} className={`border-t border-gray-50 ${isQuote ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.product.images[0]}
                          alt={name}
                          className="w-12 h-12 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900 leading-tight">{name}</p>
                          <p className="text-[11px] text-gray-400 mt-0.5">{item.product.ref}</p>
                          {item.selectedSize && (
                            <p className="text-[11px] text-gray-500">{item.selectedSize}</p>
                          )}
                          {isQuote && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">
                              {T.cart.quoteLabel}
                            </span>
                          )}
                          {/* Mobile qty */}
                          <div className="flex items-center gap-2 mt-2 sm:hidden">
                            <button onClick={() => updateQty(item.product.id, item.qty - 1)} className="w-6 h-6 rounded border border-gray-200 flex items-center justify-center">
                              <Minus size={10} />
                            </button>
                            <span className="text-sm font-semibold w-8 text-center">{item.qty}</span>
                            <button onClick={() => updateQty(item.product.id, item.qty + 1)} className="w-6 h-6 rounded border border-gray-200 flex items-center justify-center">
                              <Plus size={10} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <div className="flex items-center justify-center gap-1 border border-gray-200 rounded-lg w-fit mx-auto overflow-hidden">
                        <button
                          onClick={() => updateQty(item.product.id, item.qty - 1)}
                          className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                        >
                          <Minus size={11} />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.product.id, item.qty + 1)}
                          className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                        >
                          <Plus size={11} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-gray-600 hidden sm:table-cell">
                      {Number(item.product.price).toFixed(3)} TND
                    </td>
                    <td className="px-6 py-4 text-right">
                      {isQuote ? (
                        <span className="text-sm font-semibold text-amber-600">
                          {T.cart.quoteLabel}
                        </span>
                      ) : (
                        <span className="text-sm font-semibold text-gray-900">
                          {(Number(item.product.price) * item.qty).toFixed(3)} TND
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          {!hasQuoteLines && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
              <span className="text-sm text-gray-500">{T.cart.total}</span>
              <span className="text-xl font-bold text-gray-950">
                {Number(isMixed && mode === 'direct' ? directTotal : allTotal).toFixed(3)} TND <span className="text-xs font-normal text-gray-400">{T.common.ht}</span>
              </span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {(!isMixed || mode === 'direct') && hasDirectLines && (
              <button
                onClick={() => setPage('checkout')}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-5 bg-lodiss-red hover:bg-lodiss-dark text-white rounded-lg font-semibold text-sm transition-colors"
              >
                <ShoppingBag size={16} />
                {T.cart.checkoutDirect}
                <ArrowRight size={14} />
              </button>
            )}
            {(!isMixed || mode === 'quote' || hasQuoteLines) && (
              <button
                onClick={() => setPage('quote-request')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-5 rounded-lg font-semibold text-sm transition-colors ${
                  isMixed && mode === 'direct'
                    ? 'border-2 border-amber-400 text-amber-600 hover:bg-amber-50'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}
              >
                <FileText size={16} />
                {T.cart.checkoutQuote}
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
    </PageWrapper>
  );
}
